import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { CampaignInquiryProductInput, ReviewDestinationConfigFieldType, ReviewDestinationInput } from "src/graphql/generated/graphql";
import { ReviewDestinationType } from "src/hooks/reviews";

import { useReviewDestinations } from "../../../hooks/tenants";
import { useCreateCampaignInquiryFormContext } from "../formContext";
import { Item } from "./item";

export interface CreateCampaignInquiryProductsFormProps { }
export const CreateCampaignInquiryProductsForm = ({ }: CreateCampaignInquiryProductsFormProps) => {
    const { t } = useTranslation(["common"]);

    const createCampaignInquiryForm = useCreateCampaignInquiryFormContext();

    const { reviewDestinations } = useReviewDestinations(createCampaignInquiryForm.values?.tenantId);

    const reviewDestinationsInput: ReviewDestinationInput[] = useMemo(() => {
        return reviewDestinations.map((reviewDestination) => ({
            ...reviewDestination,
            type: reviewDestination.type || ReviewDestinationType.custom,
            configFields: reviewDestination.configFields?.map((configField) => ({
                ...configField,
                label: configField.label || "",
                path: configField.path || "",
                type: configField.type || ReviewDestinationConfigFieldType.Text,
            })),
        }));
    }, [reviewDestinations]);

    const handleAddProduct = useCallback(() => {
        const item: CampaignInquiryProductInput = {
            key: Math.random().toString(36).substring(2),
            destinations: [],
            category: null,
            ean: null,
            name: null,
            description: null,
            userManualLink: null,
            detailPageLinks: [],
            reviewFocusInstructions: null,
            thumbnailImage: null,
            images: [],
            attributes: [],
            info: {
                manufacturer: null,
                model: null,
            },
        };

        createCampaignInquiryForm.insertListItem("products", item);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createCampaignInquiryForm.values?.products?.length]);

    const handleRemoveProduct = useCallback((index: number) => {
        createCampaignInquiryForm.removeListItem("products", index);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!createCampaignInquiryForm.values?.products?.length) {
            // idk why, it just works
            // TODO: find out why
            setTimeout(handleAddProduct, 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createCampaignInquiryForm.values?.products?.length]);

    return (
        <Stack>
            {(createCampaignInquiryForm.values?.products || []).map((product, index) => {
                return (
                    <Item
                        key={`${index}${product?.key}${product?.id}`}
                        index={index}
                        reviewDestinations={reviewDestinationsInput}
                        enableCustomProductReviewDestinations={!!createCampaignInquiryForm.values?.tenantId}
                        noReviewDestinationsLabel={
                            createCampaignInquiryForm.values?.tenantId ? undefined : (
                                <Text color="red">
                                    {t("campaign_productreviewdestinations_error_missingtenant")}
                                </Text>
                            )
                        }
                        onRemove={createCampaignInquiryForm.values?.products?.length > 1 ?
                            () => handleRemoveProduct(index) : undefined}
                    />
                );
            })}
            <Button
                mt="md"
                size="lg"
                variant="light"
                leftIcon={<IconPlus />}
                onClick={handleAddProduct}
            >
                {t("product_add")}
            </Button>
        </Stack>
    );
};
